import axios from "axios";
import { API_CONFIG } from "../config/constants";
import spacesService from "./spacesService";
import cacheService from "./cacheService";

const api = axios.create({
  baseURL: API_CONFIG.FULL_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

// Projects (cached in backend)
export const getProjects = () => api.get("/projects");

export const getProject = (id) => api.get(`/projects/${id}`);
export const searchProjects = (query, filters = {}, page = 1, perPage = 10, sortBy = 'created_at', sortOrder = 'desc') => {
  const params = {
    q: query,
    page,
    per_page: perPage,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...filters
  };
  return api.get("/projects/search", { params });
};

export const unifiedSearch = (query, type = 'all', page = 1, perPage = 10, filters = {}, sortBy = 'created_at', sortOrder = 'desc') => {
  const params = {
    q: query,
    type,
    page,
    per_page: perPage,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...filters
  };
  
  // Add spatial search parameters if provided
  if (filters.lat && filters.lng) {
    params.lat = filters.lat;
    params.lng = filters.lng;
    params.radius = filters.radius || 8;
  }
  
  return api.get("/projects/unified-search", { params });
};
// Recent projects and cities (cached in backend)
export const getRecentProjects = (limit = 2) =>
  api.get("/projects/recent", { params: { limit } });
export const getCities = () => api.get("/projects/cities");
export const getDevelopers = () => api.get("/projects/developers");

// Search suggestions
export const getProjectSuggestions = (query, language = 'en', limit = 5) =>
  api.get("/search-suggestions/projects", { 
    params: { q: query, language, limit } 
  });

// Land use types (cached in backend)
export const getLandUseTypes = () => api.get("/land-use-types");
// Enhanced GeoJSON functions with DigitalOcean Spaces support
export const getProjectParcelsGeoJsonStatic = async (projectId) => {
  // Try DigitalOcean Spaces first if direct access is enabled
  if (spacesService.isDirectAccessAvailable()) {
    try {
      const spacesResult = await spacesService.fetchGeoJsonDirect(projectId, 'compressed');

      // Return in the same format as axios response
      return {
        data: spacesResult.data,
        status: 200,
        statusText: 'OK',
        headers: {
          'x-source': 'digitalocean-spaces',
          'x-cached': spacesResult.cached ? 'HIT' : 'MISS'
        },
        config: { url: spacesResult.url }
      };
    } catch (spacesError) {
      console.warn(`⚠️ Spaces request failed for project ${projectId}, falling back to API:`, spacesError.message);
      // Fall through to API fallback
    }
  } else {
  }

  // Fallback to API endpoint
  return api.get(`/projects/${projectId}/parcels/geojson-static`);
};

export const getProjectParcelsGeoJsonCached = async (projectId) => {
  // Try DigitalOcean Spaces first if direct access is enabled
  if (spacesService.isDirectAccessAvailable()) {
    try {
      const spacesResult = await spacesService.fetchGeoJsonDirect(projectId, 'compressed');

      return {
        data: spacesResult.data,
        status: 200,
        statusText: 'OK',
        headers: {
          'x-source': 'digitalocean-spaces',
          'x-cached': spacesResult.cached ? 'HIT' : 'MISS'
        },
        config: { url: spacesResult.url }
      };
    } catch (spacesError) {
      console.warn(`⚠️ Spaces cached request failed for project ${projectId}, falling back to API:`, spacesError.message);
    }
  }

  // Fallback to API endpoint
  return api.get(`/projects/${projectId}/parcels/geojson-cached`);
};

export const getProjectParcelsGeoJsonUltra = async (projectId) => {
  // Try DigitalOcean Spaces first if direct access is enabled
  if (spacesService.isDirectAccessAvailable()) {
    try {
      const spacesResult = await spacesService.fetchGeoJsonDirect(projectId, 'ultra');

      return {
        data: spacesResult.data,
        status: 200,
        statusText: 'OK',
        headers: {
          'x-source': 'digitalocean-spaces',
          'x-cached': spacesResult.cached ? 'HIT' : 'MISS',
          'content-encoding': 'gzip'
        },
        config: { url: spacesResult.url }
      };
    } catch (spacesError) {
      console.warn(`⚠️ Spaces ultra request failed for project ${projectId}, falling back to API:`, spacesError.message);
    }
  }

  // Fallback to API endpoint
  return api.get(`/projects/${projectId}/parcels/geojson-ultra`);
};

// Utility functions for DigitalOcean Spaces
export const checkSpacesAvailability = () => {
  return spacesService.isDirectAccessAvailable();
};

export const getSpacesConfig = () => {
  return spacesService.getConfig();
};

export const getDirectGeoJsonUrl = (projectId, type = 'compressed') => {
  return spacesService.getDirectUrl(projectId, type);
};

// Batch prefetch function for multiple projects
export const prefetchProjectsGeoJson = async (projectIds, type = 'compressed') => {
  if (spacesService.isDirectAccessAvailable()) {
    try {
      return await spacesService.prefetchGeoJsonBatch(projectIds, type);
    } catch (error) {
      console.warn('Batch prefetch from Spaces failed:', error);
    }
  }

  // Fallback: fetch individually through API
  const results = {};
  const promises = projectIds.map(async (projectId) => {
    try {
      const response = await getProjectParcelsGeoJsonStatic(projectId);
      results[projectId] = { data: response.data, source: 'api' };
    } catch (error) {
      results[projectId] = { error: error.message };
    }
  });

  await Promise.allSettled(promises);
  return results;
};

// Blocks with caching
export const getBlocks = async (projectId) => {
  const cacheKey = cacheService.generateKey("/blocks", { project_id: projectId });
  const cached = cacheService.get(cacheKey);

  if (cached) {
    return cached;
  }

  return cacheService.deduplicate(cacheKey, async () => {
    const response = await api.get("/blocks", { params: { project_id: projectId } });
    cacheService.set(cacheKey, response, 24 * 60 * 60 * 1000); // Cache for 1 day
    return response;
  });
};

export const getBlock = (id) => api.get(`/blocks/${id}`);
export const getBlocksGeoJson = (projectId) =>
  api.get(`/projects/${projectId}/blocks/geojson`);

// Parcels
export const getParcels = (blockId) =>
  api.get("/parcels", { params: { block_id: blockId } });
export const getParcel = (id) => api.get(`/parcels/${id}`);
export const getParcelWithProject = (id, options = {}) => {
  const authHeaders = getAuthHeaders();

  return api.get(`/parcels/${id}/with-project`, {
    ...options,
    headers: { ...options.headers, ...authHeaders }
  });
};
export const getParcelsGeoJson = (blockId) =>
  api.get(`/blocks/${blockId}/parcels/geojson`);
export const searchParcels = (query, filters = {}, page = 1, perPage = 10, sortBy = 'created_at', sortOrder = 'desc', projectId = null) => {
  const params = {
    q: query,
    page,
    per_page: perPage,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...filters
  };

  // Add project_id if provided
  if (projectId) {
    params.project_id = projectId;
  }

  return api.get("/parcels/search", { params });
};

// Map Layers with caching
export const getMapLayers = (projectId) =>
  api.get("/map-layers", { params: { project_id: projectId } });
export const getMapLayer = (id) => api.get(`/map-layers/${id}`);

// Floors API (for residential projects) with caching
export const getProjectFloors = async (projectId) => {
  const cacheKey = cacheService.generateKey(`/projects/${projectId}/floors`);
  const cached = cacheService.get(cacheKey);

  if (cached) {
    return cached;
  }

  return cacheService.deduplicate(cacheKey, async () => {
    const response = await api.get(`/projects/${projectId}/floors`);
    cacheService.set(cacheKey, response, 10 * 60 * 1000); // Cache for 10 minutes
    return response;
  });
};

export const getFloor = (id) => api.get(`/floors/${id}`);
export const getFloors = (projectId = null) => {
  const params = projectId ? { project_id: projectId } : {};
  return api.get("/floors", { params });
};

export const getFloorUnitsGeoJson = async (floorId) => {
  const cacheKey = cacheService.generateKey(`/floors/${floorId}/units/geojson`);
  // const cached = cacheService.get(cacheKey);

  // if (cached) {
  //   return { data: cached };
  // }

  return cacheService.deduplicate(cacheKey, async () => {
    const response = await api.get(`/floors/${floorId}/units/geojson`);
    const data = response.data;
    cacheService.set(cacheKey, data, 5 * 60 * 1000); // Cache for 5 minutes
    return { data: data };
  });
};

export const getFloorBasemap = async (floorId) => {
  const cacheKey = cacheService.generateKey(`/floors/${floorId}/basemap`);
  const cached = cacheService.get(cacheKey);

  if (cached) {
    return cached;
  }

  return cacheService.deduplicate(cacheKey, async () => {
    const response = await api.get(`/floors/${floorId}/basemap`);
    cacheService.set(cacheKey, response, 120 * 60 * 1000); // Cache for 10 minutes
    return response;
  });
};
export const getProjectMapLayers = async (projectId) => {
  const cacheKey = cacheService.generateKey(`/projects/${projectId}/map-layers`);
  const cached = cacheService.get(cacheKey);

  if (cached) {
    return cached;
  }

  return cacheService.deduplicate(cacheKey, async () => {
    const response = await api.get(`/projects/${projectId}/map-layers`);
    cacheService.set(cacheKey, response, 10 * 60 * 1000); // Cache for 10 minutes
    return response;
  });
};

// Landmarks with caching
export const getLandmarks = (projectId) =>
  api.get("/landmarks", { params: { project_id: projectId } });
export const getLandmark = (id) => api.get(`/landmarks/${id}`);

export const getProjectLandmarks = async (projectId) => {
  const cacheKey = cacheService.generateKey(`/projects/${projectId}/landmarks`);
  const cached = cacheService.get(cacheKey);

  if (cached) {
    return cached;
  }

  return cacheService.deduplicate(cacheKey, async () => {
    const response = await api.get(`/projects/${projectId}/landmarks`);
    cacheService.set(cacheKey, response, 10 * 60 * 1000); // Cache for 10 minutes
    return response;
  });
};

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Favorites
export const getFavorites = () =>
  api.get("/favorites", { headers: getAuthHeaders() });

export const addToFavorites = (parcelId) =>
  api.post("/favorites", { parcel_id: parcelId }, { headers: getAuthHeaders() });

export const removeFromFavorites = (parcelId) =>
  api.delete(`/favorites/${parcelId}`, { headers: getAuthHeaders() });

export const checkFavoriteStatus = (parcelId) =>
  api.get(`/favorites/check/${parcelId}`, { headers: getAuthHeaders() });

export const toggleFavorite = (parcelId) =>
  api.post("/favorites/toggle", { parcel_id: parcelId }, { headers: getAuthHeaders() });

// Land Use Types - moved to cached section above

export default api;
